import React, { useState } from 'react';
import { Button, Form, Modal, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CardSkeleton } from '@/components/skeleton';
import { CategoryForm } from './helper/form';
import { CategoryCard } from './helper/card';
import { ICategory, ICategoryForm } from '../../types/categorySetup';
import {
  useCategoryList,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '../../hooks/categorySetup';

export const CategoriesSetup: React.FC = () => {
  const [form] = Form.useForm<ICategoryForm>();
  const [showEditor, setShowEditor] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ICategory | null>(null);

  // API Hooks with pagination params
  const { isLoading, data } = useCategoryList({
    current: 1,
    pageSize: 100,
  });
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.dogma_categoryid,
          payload: values,
        });
      } else {
        await createCategory.mutateAsync(values);
      }

      setShowEditor(false);
      form.resetFields();
      setEditingCategory(null);
    } catch (error) {
    }
  };

  const handleEdit = (category: ICategory) => {
    setEditingCategory(category);
    form.setFieldsValue({
      dogma_categorytype: category.dogma_categorytype,
      dogma_name: category.dogma_name,
      dogma_description: category.dogma_description,
      dogma_order: category.dogma_order,
    });
    setShowEditor(true);
  };

  const handleDelete = async (categoryId: string) => {
    await deleteCategory.mutateAsync(categoryId);
  };

  const handleCancel = () => {
    setShowEditor(false);
    form.resetFields();
    setEditingCategory(null);
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    form.setFieldsValue({
      dogma_categorytype: 1,
      dogma_name: '',
      dogma_description: '',
    });
    setShowEditor(true);
  };

  return (
    <>
      <div className='bg-white rounded-lg p-4'>
        <div className="flex justify-between items-center mb-4">
          <h2 className='text-lg font-semibold text-text-primary'>Configuration Categories</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add Category
          </Button>
        </div>

        {isLoading ? (
          <CardSkeleton count={4} />
        ) : data?.value && data.value.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.value.map((category) => (
              <CategoryCard
                key={category.dogma_categoryid}
                category={category}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <Empty description="No categories added yet" />
        )}
      </div>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={showEditor}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={createCategory.isPending || updateCategory.isPending}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <CategoryForm />
        </Form>
      </Modal>
    </>
  );
};
